import { IShoppingProduct } from '@inter-ikea-kompis/types';
import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { captureException } from '@sentry/react';
import { DoorHeightEnum } from 'CoreAPI/Components/DoorHeightEnum';
import { DoorFrameColorEnum } from 'products/doorframes';
import { IShoppingProductsMap, IVisibleProducts } from 'utils/constants';
import { isShallowIdentical } from 'utils/isShallowIdentical';
import { fetchProductsData } from './products.thunks';

export interface IMissingSkyttaProducts {
  railColors: DoorFrameColorEnum[];
  lowDoorColors: DoorFrameColorEnum[];
  highDoorColors: DoorFrameColorEnum[];
}

type State = {
  allProducts: IShoppingProductsMap;
  allValidSPRs: IShoppingProduct[];
  visibleProducts: IVisibleProducts;
  missingSkyttaProducts: IMissingSkyttaProducts;
  doorHeightOptionsAvailable: DoorHeightEnum[];
  activeProductId?: string;
};

const initialState: State = {
  allProducts: {},
  allValidSPRs: [],
  visibleProducts: {},
  missingSkyttaProducts: {
    railColors: [],
    lowDoorColors: [],
    highDoorColors: []
  },
  doorHeightOptionsAvailable: [],
  activeProductId: undefined
};

export const { reducer: productsReducer, actions: productAction } = createSlice({
  name: 'products',
  initialState,
  extraReducers: builder => {
    builder.addCase(fetchProductsData.rejected, (state, action) => {
      const error = new Error();
      error.name = 'Failed to fetch product data';
      error.message = action.error.message || '';

      captureException(error, scope => {
        scope.setExtra('state', state);
        scope.setExtra('action', action);
        return scope;
      });
    });
  },
  reducers: {
    setProducts: (state, { payload: allProducts }: PayloadAction<State['allProducts']>) => {
      if (isShallowIdentical(current(state).allProducts, allProducts)) {
        return;
      }

      return {
        ...state,
        allProducts
      };
    },
    setAllValidSPRs: (state, { payload: allValidSPRs }: PayloadAction<State['allValidSPRs']>) => {
      if (isShallowIdentical(current(state).allValidSPRs, allValidSPRs)) {
        return;
      }

      return {
        ...state,
        allValidSPRs
      };
    },
    setVisibleProducts: (
      state,
      { payload: visibleProducts }: PayloadAction<State['visibleProducts']>
    ) => {
      if (isShallowIdentical(current(state).visibleProducts, visibleProducts)) {
        return;
      }

      return { ...state, visibleProducts };
    },
    setActiveProductId: (
      state,
      { payload: activeProductId }: PayloadAction<State['activeProductId']>
    ) => {
      if (isShallowIdentical(current(state).activeProductId, activeProductId)) {
        return;
      }

      return { ...state, activeProductId };
    },
    setMissingSkyttaProducts: (
      state,
      { payload: missingSkyttaProducts }: PayloadAction<State['missingSkyttaProducts']>
    ) => {
      if (isShallowIdentical(current(state).missingSkyttaProducts, missingSkyttaProducts)) {
        return;
      }

      return { ...state, missingSkyttaProducts };
    },
    setDoorHeightOptionsAvailable: (
      state,
      { payload: doorHeightOptionsAvailable }: PayloadAction<State['doorHeightOptionsAvailable']>
    ) => {
      if (
        isShallowIdentical(current(state).doorHeightOptionsAvailable, doorHeightOptionsAvailable)
      ) {
        return;
      }

      return { ...state, doorHeightOptionsAvailable };
    }
  }
});
